import React, { useState, useEffect } from "react";
import "./App.css";

import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";

import { auth } from "./Login/firebase";
import apiClient from "./utils/apiClient";
import LoginPage from "./Login/Login";
import Dashboard from "./Dashboard/Dashboard";
import DashboardLive from "./Dashboard/Live/DashboardLive";
import DashboardHistory from "./Dashboard/History/DashboardHistory";
import Users from "./Dashboard/Users/Users";
import Settings from "./Dashboard/Settings/Settings";
import OrderPlacement from "./Dashboard/OrderPlacement/OrderPlacement";
import StraddleDashboard from "./Dashboard/StraddleTable/StraddleDashboard";
import StrategyWise from "./Dashboard/StrategyWise/StrategyWise";
import PortfolioAnalyser from "./Dashboard/PortfolioAnalyser/portfolioAnalyser";
import SSStrategy from "./Dashboard/SSStrategy/Strategy";
import GreeksComponent from "./Dashboard/Greeks/GreeksComponent";
import StrategyFirstTable from "./Dashboard/SSStrategy/StrategyFirstTable";
import AlphaStrategy from "./Dashboard/SSStrategy/AlphaStrategy";
import PdfToExcel from "./Dashboard/PDF-toExcel/pdfToExcel";
import DataVisualizer from "./Dashboard/DataVisualizer/DataVisualizer";
import SourceTagsComponent from "./Dashboard/SourceTags/SourceTags";
import LiveMetricsDashboard from "./Dashboard/LiveMetricsDashboard";
import BotConfig from "./Dashboard/BotConfigs/BotConfig";
import StocksScreener from "./Dashboard/StocksScreener/StocksScreener";
import EventsCalendar from "./Dashboard/EventsCalendar/EventsCalendar";
import AlphaScreener from "./Dashboard/AlphaScreener/AlphaScreener";
import StockChart from "./Dashboard/Chart/new-stock";
import CommodityTrades from "./Dashboard/CommodityTrades/commodityTrades";
import RawOrdersPositions from "./Dashboard/RawOrdersPositions/RawOrdersPositions";
// import { GlobalPositionsProvider } from "./hooks/global-positions"; // REMOVED - now using GlobalDataProvider only
import { GlobalDataProvider } from "./contexts/GlobalDataContext";
import NewPositionAndOrderView from "./Dashboard/NewPositionAndOrderView/NewPositionAndOrderView.jsx";
import Audit from "./Dashboard/Audit/Audit.jsx";
import RelativeStrength from "./Dashboard/RelativeStrength/RelativeStrength.jsx";
import MarketBreadth from "./Dashboard/MarketBreadth/MarketBreadth.jsx";
import WebSocketInitializer from "./contexts/websocketInitializer.js";

import OpportunityScreener from "./Dashboard/OpportunityScreener/OpportunityScreener.jsx";
import ClearStocks from "./Dashboard/ClearStocks/ClearStocks.jsx";
import AdminPanel from "./Dashboard/AdminPanel/AdminPanel.jsx";
import CRM from "./Dashboard/CRM/CRM.jsx";
import MessagesDashboard from "./Dashboard/Messages/MessagesDashboard.jsx";
import GodsEyeLoadingScreen from "./components/GodsEyeLoadingScreen";
const OperationsPage = React.lazy(() => import("./Dashboard/Operations/OperationsPage"));

const getDefaultRoute = () => {
  const cachedAccess = localStorage.getItem("user_access");
  const userAccess = cachedAccess ? JSON.parse(cachedAccess) : null;

  if (!userAccess) return "/live-position-view";

  if (userAccess.role === "admin") {
    return "/admin-panel";
  }

  const routesToCheck = [
    { permission: "is_live_position_view_access", path: "/live-position-view" },
    { permission: "is_audit_access", path: "/audit" },
    { permission: "is_opportunity_screener_access", path: "/opportunity-screener" },
    { permission: "is_dashboard_access", path: "/dashboard" },
    { permission: "is_live_metrics_access", path: "/live-metrics" },
    { permission: "is_order_management_access", path: "/live-positions" },
    { permission: "is_source_tags_access", path: "/source-tags" },
    { permission: "is_portfolio_analyser_access", path: "/portfolio-analyser" },
    { permission: "is_bot_config_access", path: "/bot-config" },
    { permission: "is_alpha_screener_access", path: "/alpha-screener" },
    { permission: "is_data_visualizer_access", path: "/data-visualizer" },
    { permission: "is_events_calendar_access", path: "/events-calendar" },
    { permission: "is_tv_options_chart_access", path: "/tv-option-chart" },
    { permission: "is_commodity_trades_access", path: "/commodity-trades" },
    { permission: "is_relative_strength_access", path: "/relative-strength" },
    { permission: "is_market_breadth_access",    path: "/market-breadth" },
    { permission: "is_clear_stocks_access", path: "/clear-stocks" },
    { permission: "is_crm_tab", path: "/crm" },
  ];

  for (const route of routesToCheck) {
    const val = userAccess[route.permission];
    if (val !== false && val !== null && val !== undefined) {
      return route.path;
    }
  }

  return "/settings";
};

const ProtectedRoute = ({ children, permissionField, requireAdmin, ...rest }) => {
  const cachedAccess = localStorage.getItem("user_access");
  const userAccess = cachedAccess ? JSON.parse(cachedAccess) : null;

  if (requireAdmin && (!userAccess || userAccess.role !== "admin")) {
    return <Redirect to={getDefaultRoute()} />;
  }

  if (permissionField && (!userAccess || userAccess[permissionField] === false || userAccess[permissionField] === null || userAccess[permissionField] === undefined)) {
    return <Redirect to={getDefaultRoute()} />;
  }

  return <Route {...rest}>{children}</Route>;
};

function App(props) {
  const [user, loading] = useAuthState(auth);
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [isLoadingEmails, setIsLoadingEmails] = useState(true);
  const [userAccess, setUserAccess] = useState(null);
  const [isLoadingAccess, setIsLoadingAccess] = useState(true);

  React.useEffect(() => {
    const fetchAllowedEmails = async () => {
      try {
        const response = await apiClient.get("allowed-emails");
        if (response.data && response.data.status === "success") {
          setAllowedEmails(response.data.emails);
        }
      } catch (error) {
        console.error("Error fetching allowed emails:", error);
      } finally {
        setIsLoadingEmails(false);
      }
    };
    fetchAllowedEmails();
  }, []);

  React.useEffect(() => {
    const fetchUserAccess = async () => {
      if (user && user.email) {
        setIsLoadingAccess(true);
        try {
          const response = await apiClient.get(`user-access?email=${user.email}`);
          if (response.data && response.data.status === "success") {
            setUserAccess(response.data.access);
            localStorage.setItem("user_access", JSON.stringify(response.data.access));
          }
        } catch (error) {
          console.error("Error fetching user access in App:", error);
          const cached = localStorage.getItem("user_access");
          if (cached) {
            setUserAccess(JSON.parse(cached));
          }
        } finally {
          setIsLoadingAccess(false);
        }
      } else {
        setUserAccess(null);
        setIsLoadingAccess(false);
      }
    };

    if (!loading) {
      fetchUserAccess();
    }
  }, [user, loading]);

  React.useEffect(() => {
    if (user && user.email) {
      localStorage.setItem("user_email", user.email);
    } else if (!loading) {
      localStorage.removeItem("user_email");
    }
  }, [user, loading]);

  if (loading || isLoadingEmails || (user && isLoadingAccess)) {
    return <GodsEyeLoadingScreen />;
  }

  const isUserAllowed = user && user.email && user.email.split("@")[1] === "iamclearmind.com" && allowedEmails.includes(user.email);

  return (
    <div className="flex flex-col min-h-screen font-urbanist text-[#40414f] w-full mx-auto ">
      <GlobalDataProvider>
        <WebSocketInitializer />
        <Router>
          <Switch>
            <Route path="/relative-strength" exact>
              <RelativeStrength />
            </Route>
            {isUserAllowed ? (
              <Switch>
                <ProtectedRoute path="/operations" exact><Redirect to="/monitoring" /></ProtectedRoute>
                <ProtectedRoute path="/monitoring" exact>
                  <React.Suspense fallback={<GodsEyeLoadingScreen />}><OperationsPage /></React.Suspense>
                </ProtectedRoute>
                <ProtectedRoute path="/clear-stocks" permissionField="is_clear_stocks_access" exact>
                  <ClearStocks />
                </ProtectedRoute>
                <ProtectedRoute path="/admin-panel" requireAdmin={true} exact>
                  <AdminPanel />
                </ProtectedRoute>
                <ProtectedRoute path="/crm" permissionField="is_crm_tab" exact>
                  <CRM />
                </ProtectedRoute>
                <ProtectedRoute path="/messages" permissionField="is_crm_tab" exact>
                  <MessagesDashboard />
                </ProtectedRoute>
                <ProtectedRoute path="/commodity-trades" permissionField="is_commodity_trades_access" exact>
                  <CommodityTrades />
                </ProtectedRoute>
                <ProtectedRoute path="/audit" permissionField="is_audit_access" exact>
                  <Audit />
                </ProtectedRoute>

                <ProtectedRoute path="/market-breadth" permissionField="is_market_breadth_access" exact>
                  <MarketBreadth />
                </ProtectedRoute>

                <ProtectedRoute path="/tv-option-chart" permissionField="is_tv_options_chart_access" exact>
                  <StockChart />
                </ProtectedRoute>
                <Route path="/raw-orders-positions" exact>
                  <RawOrdersPositions />
                </Route>
                <ProtectedRoute path="/data-visualizer" permissionField="is_data_visualizer_access" exact>
                  <DataVisualizer />
                </ProtectedRoute>
                <Route path="/greeks" exact>
                  <GreeksComponent />
                </Route>
                <ProtectedRoute path="/alpha-screener" permissionField="is_alpha_screener_access" exact>
                  <AlphaScreener />
                </ProtectedRoute>
                <ProtectedRoute path="/opportunity-screener" permissionField="is_opportunity_screener_access" exact>
                  <OpportunityScreener />
                </ProtectedRoute>
                <ProtectedRoute path="/events-calendar" permissionField="is_events_calendar_access" exact>
                  <EventsCalendar />
                </ProtectedRoute>
                <ProtectedRoute path="/bot-config" permissionField="is_bot_config_access" exact>
                  <BotConfig />
                </ProtectedRoute>
                <Route path="/stocks-screener" exact>
                  <StocksScreener />
                </Route>
                <ProtectedRoute path="/source-tags" permissionField="is_source_tags_access" exact>
                  <SourceTagsComponent />
                </ProtectedRoute>
                <Route path="/strategy-list" exact>
                  <AlphaStrategy />
                </Route>
                <Route path="/strategy-first-table" exact>
                  <StrategyFirstTable />
                </Route>

                <Route path="/ss-strategy" exact>
                  <SSStrategy />
                </Route>
                <ProtectedRoute path="/portfolio-analyser" permissionField="is_portfolio_analyser_access" exact>
                  <PortfolioAnalyser />
                </ProtectedRoute>
                <Route path="/pdf-to-excel" exact>
                  <PdfToExcel />
                </Route>

                <ProtectedRoute path="/order-placement" permissionField="is_order_management_access" exact>
                  <OrderPlacement />
                </ProtectedRoute>

                <Route path="/settings" exact>
                  <Settings />
                </Route>

                <Route path="/users" exact>
                  <Users />
                </Route>
                {/* <Route path="/vma" exact>
                  <VMA />
                </Route> */}
                {/* <Route path="/fyers-user-list" exact>
                  <FyersUserList />
                </Route> */}
                <Route path="/strategy-wise" exact>
                  <StrategyWise />
                </Route>
                <Route path="/straddle-table" exact>
                  <StraddleDashboard />
                </Route>
                <Route path="/history" exact>
                  <DashboardHistory />
                </Route>
                <ProtectedRoute path="/live-positions" permissionField="is_order_management_access" exact>
                  <DashboardLive />
                </ProtectedRoute>
                <ProtectedRoute path="/live-metrics" permissionField="is_live_metrics_access" exact>
                  <LiveMetricsDashboard />
                </ProtectedRoute>
                <ProtectedRoute path="/live-position-view" permissionField="is_live_position_view_access" exact>
                  <NewPositionAndOrderView />
                </ProtectedRoute>
                <Route exact path="/">
                  <Redirect to={getDefaultRoute()} />
                </Route>
                <ProtectedRoute path="/dashboard" permissionField="is_dashboard_access">
                  <Dashboard />
                </ProtectedRoute>
              </Switch>
            ) : (
              <LoginPage />
            )}
          </Switch>
        </Router>
      </GlobalDataProvider>
    </div>
  );
}

export default App;
